import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Employee } from '../../../models/employee';
import { combineLatest, finalize, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import { Tenant } from '../../../models/tenant';
import { EmployeeService } from '../../../services/employee.service';
import { roles } from '../../../utils/enums/roles';
import { environment } from '../../../../environments/environment';
import { duplicatedValidator } from '../../../utils/validators/duplicateValidator';
import moment from 'moment';
import _ from 'lodash';

@Component({
  selector: 'app-employee-drawer',
  standalone: false,
  templateUrl: './employee-drawer.component.html',
  styleUrl: './employee-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class EmployeeDrawerComponent implements OnInit, OnDestroy {
  @Input() employee: Employee;
  @Input() tenant: Tenant;

  public _ = _;
  public adminOptions: {name: string, value: boolean}[] = [];
  private autogeneratedUsername: boolean;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public employeeAvatar: string;
  public employeeAvatarBlob: Blob;
  public form: FormGroup;
  public loading: boolean;
  public loadingAvatar: boolean = true;
  public loadingUsername: boolean;
  public moment = moment;
  public occupationTypes: {value: string, name: string}[] = [];
  public roles: {id: string; name: string;}[] = [];
  public vacationTypes: {value: string, name: string}[] = [];

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private auxiliarService: AuxiliarService,
    private logger: NGXLogger,
    private translate: TranslateService,
    private employeeService: EmployeeService,
  ){}

  ngOnInit(): void {
    // roles
    this.roles = _.map(roles, (role: string) => {
      return { id: role, name: this.translate.instant(`roles.${role}`) };
    });
    // occupation types options
    this.occupationTypes = this.auxiliarService.getOccupationTypesOptions();
    // admin options
    this.adminOptions = this.auxiliarService.getBinaryOptions();
    // vacation types options
    this.vacationTypes = _.orderBy(this.auxiliarService.getVacationTypes(), 'name');
    // auto generated username
    this.autogeneratedUsername = !this.employee;
    // load form
    this.loadForm();
    // get employee avatar
    this.getEmployeeAvatar();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadForm(): void {
    // employee roles
    const employeeRoles = _.split(_.get(_.first(this.employee?.roles), 'name'), ',');
    const rolesGroup: { [key: string]: FormControl } = {};
    this.roles.forEach(role => {
      rolesGroup[role.id] = new FormControl(_.includes(employeeRoles, role.id));
    });
    // occupation (if professional)
    const occupation = _.get(_.first(this.employee?.roles), 'occupation');

    this.form = this.formBuilder.group({
      daysOfVacations: [this.employee?.daysOfVacations || '', []],
      daysOfVacationsThisYear: [this.employee?.daysOfVacationsThisYear || '', []],
      firstname: [this.employee?.person?.firstname || '', [Validators.required]],
      isAdmin: [_.includes(employeeRoles, 'admin'), []],
      lastname: [this.employee?.person?.lastname || '', [Validators.required]],
      email: [this.employee?.person?.email || '', [Validators.email]],
      id: [this.employee?.id, []],
      occupation: [_.find(this.occupationTypes, {value: occupation}) || '', []],
      phone: [this.formatPhone(this.employee?.person?.phone), [Validators.pattern(environment.pattern.phone)]],
      professionalAssociation: [this.employee?.person?.professionalAssociation, []],
      professionalCode: [this.employee?.person?.professionalCode, []],
      roles: this.formBuilder.group(rolesGroup),
      tenant: [{value: this.tenant?.name, disabled: true}, []],
      typeOfVacations: [_.find(this.vacationTypes, {value: this.employee?.typeOfVacations}) || '', []],
      username: [{value: this.employee?.username || '', disabled: this.employee}, [Validators.required]],
    });

    if(this.employee) {
      this.f['id'].setValidators([Validators.required]);
    }

    // username value change to check if username has been modified by hand by user 
    this.f['username'].valueChanges.subscribe(() => {
      if (this.f['username'].dirty) {
        this.autogeneratedUsername = false;
      }
    });

    // firstname / lastname value change to autogenerate username if it has not been modified by hand by user
    combineLatest({
      firstname: this.f['firstname'].valueChanges.pipe(startWith(this.f['firstname'].value)) as Observable<string>,
      lastname: this.f['lastname'].valueChanges.pipe(startWith(this.f['lastname'].value)) as Observable<string>,
    }).subscribe(({ firstname, lastname }) => {
      if (this.autogeneratedUsername && firstname && lastname) {
        const username = _.toLower(_.deburr(`${_.replace(firstname, /\s/g, '')}.${_.first(_.split(lastname, ' '))}`));
        this.f['username'].setValue(username, { emitEvent: false });
      }
    });

    // roles value change to check if vacations fields are required 
    this.f['roles'].valueChanges.subscribe((value: any) => {
      if(!this.employee) { // TODO remove this condition when back ready
        if(value?.employee) {
          this.f['daysOfVacationsThisYear'].setValidators([Validators.required]);
          this.f['daysOfVacations'].setValidators([Validators.required]);
          this.f['daysOfVacations'].setValue(environment.vacations.defaultDays);
          this.f['typeOfVacations'].setValidators([Validators.required]);
          this.f['typeOfVacations'].setValue(_.find(this.vacationTypes, {value: environment.vacations.defaultType}));
        } else {
          // send default values to backend
          this.f['daysOfVacationsThisYear'].setValidators([]);
          this.f['daysOfVacationsThisYear'].setValue(0);
          this.f['daysOfVacations'].setValidators([]);
          this.f['daysOfVacations'].setValue(0);
          this.f['typeOfVacations'].setValidators([]);
          this.f['typeOfVacations'].setValue(_.find(this.vacationTypes, {value: environment.vacations.defaultType}));
        }
        this.f['daysOfVacationsThisYear'].updateValueAndValidity();
        this.f['daysOfVacations'].updateValueAndValidity();
        this.f['typeOfVacations'].updateValueAndValidity();
      }
    });
  }

  private formatPhone(phone: string): string {
    if(!phone) return '';
    return _.replace(phone, '+34', '');
  }

  public checkUsername(): void {
    if(!this.f['username'].value || this.f['username'].value === this.employee?.username) {
      this.f['username'].setValidators([Validators.required]);
      this.f['username'].updateValueAndValidity();
      return;
    };
    this.loadingUsername = true;
    this.employeeService.checkEmployeeUsername(this.f['username'].value).pipe(
      finalize(() => this.loadingUsername = false),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.logger.debug('[employeeService] checkEmployeeUsername res: ', res);
        // set validators
        if(!_.isEmpty(_.get(res, 'body.data'))) {
          this.f['username'].setValidators([duplicatedValidator(_.castArray(_.get(res, 'body.data.username')),
            this.translate.instant('error.employee-username-duplicated', {
              username: this.f['username'].value,
            })
          )]);
        } else {
          this.f['username'].setValidators([Validators.required]);
        }
        this.f['username'].updateValueAndValidity();
      },
      error: (err: any) => {
        this.logger.error('[employeeService] checkEmployeeUsername err:', err);
      }
    });
  }

  private getEmployeeAvatar(): void {
    if(!this.employee) {
      this.employeeAvatar = this.auxiliarService.generateInitialsAvatar('', 200);
      this.loadingAvatar = false;
    } else {
      this.employeeService.getEmployeeAvatar(this.employee.id).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: async (res: any) => {
          this.logger.debug('[employeeService] getEmployeeAvatar res: ', res);
          const avatarBlob = _.get(res, 'body');
          if(avatarBlob?.size > 0) {
            const avatar = await this.auxiliarService.convertBlobToBase64(avatarBlob);
            this.employeeAvatar = avatar || this.auxiliarService.generateInitialsAvatar(this.employee.person?.givenname, 200);
          } else {
            this.employeeAvatar = this.auxiliarService.generateInitialsAvatar(this.employee.person?.givenname, 200);
          }
          this.loadingAvatar = false;
        },
        error: (err: any) => {
          this.logger.error('[employeeService] getEmployeeAvatar err:', err);
          this.employeeAvatar = this.auxiliarService.generateInitialsAvatar(this.employee.person?.givenname, 200);
          this.loadingAvatar = false;
        }
      });
    }
  }

  public setEmployeeAvatar($event: { photo: string, blob: Blob }): void {
    this.employeeAvatar = $event.photo;
    this.employeeAvatarBlob = $event.blob;
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.loading = true;
      const formData = _.cloneDeep(this.form.getRawValue());
      _.set(formData, 'rolesArray', _.keys(_.pickBy(this.f['roles'].value, Boolean)));
      _.set(formData, 'occupation', _.get(formData, 'occupation.value', ''));
      _.set(formData, 'typeOfVacations', _.get(formData, 'typeOfVacations.value', formData.typeOfVacations));
      _.set(formData, 'person', {
        email: formData.email,
        firstname: formData.firstname,
        lastname: formData.lastname,
        phone: formData.phone,
        professionalAssociation: formData.professionalAssociation,
        professionalCode: formData.professionalCode,
      });
      if(this.f['isAdmin'].value) {
        formData.rolesArray.push('admin');
      }
      delete formData.isAdmin;
      if(this.employee) {
        this.employeeService.updateEmployee(formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[employeeService] updateEmployee res: ', res);
            if(this.employeeAvatarBlob?.size) {
              this.updateEmployeeAvatar(_.get(res, 'body.data'));
            } else {
              this.auxiliarService.closeDrawer('right', _.get(res, 'body.data'));
            }
            this.auxiliarService.toast(this.translate.instant('success.employee-update'), 'success');
          },
          error: (err: any) => {
            this.logger.error('[employeeService] updateEmployee err:', err);
            this.auxiliarService.toast(this.translate.instant('error.employee-update'), 'error');
          }
        });
      } else {
        delete formData.id;
        this.employeeService.createEmployee(formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[employeeService] createEmployee res: ', res);
            if(this.employeeAvatarBlob?.size) {
              this.updateEmployeeAvatar(_.get(res, 'body.data'));
            } else {
              this.auxiliarService.closeDrawer('right', _.get(res, 'body.data'));
            }
            this.auxiliarService.toast(this.translate.instant('success.employee-create'), 'success');
          },
          error: (err: any) => {
            this.logger.error('[employeeService] createEmployee err:', err);
            this.auxiliarService.toast(this.translate.instant('error.employee-create'), 'error');
          }
        });
      }
    }
  }

  public updateEmployeeAvatar(employee: Employee): void {
    this.employeeService.uploadEmployeeAvatar(employee, this.employeeAvatarBlob).pipe(
      finalize(() => this.auxiliarService.closeDrawer('right', employee)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.logger.debug('[employeeService] uploadEmployeeAvatar res: ', res);
      },
      error: (err: any) => {
        this.logger.error('[employeeService] uploadEmployeeAvatar err:', err);
        this.auxiliarService.toast(this.translate.instant('error.employee-avatar'), 'error');
      }
    });
  }

  public cancel(): void {
    this.auxiliarService.closeDrawer('right');
  }
}
